import { defineStore, acceptHMRUpdate } from 'pinia'
import * as taskAPI from '../scripts/task'
import * as isoCtrl from '../scripts/isoCtrl'
import moment from 'moment'
import { saveAs } from 'file-saver'
import { useTasksFilterStore } from './tasksFilter'

export const useTasksStore = defineStore('tasks', {
    state: () => ({
        loading: true,
        loadingPlans: false,
        uploadISODataDialog: false,
        uploadISODataTaskId: null,
        createNewTask: false,
        creatingNewTask: false,
        field: null,
        tasks: {},
        activeTasksPeriod: '6m',
        // Tasks grouped by period (e.g. "7d", "30d", "90d", "6m", "1y", "all")
        tasksByPeriod: {},
        // Loading flags per period
        loadingPeriods: {},
        editTaskDialog: false,
        editFieldDialog: false,
        editTask: null,
        editField: null,
        disabledTasks: [],
        isListOpened: false,
        routePlannerChecked: false, // Flag to prevent repeated route planner checks
        routePlannerTask: null,
        pendingScrollTaskId: null,
        pendingScrollCheck: false, // also run Check as-applied after scroll
        pendingScrollNexatMachineId: null,
    }),
    getters: {
        sortedTasks: (state) => {
            return Object.values(state.tasks).sort((a, b) => b.id - a.id)
        },
        selectedPlan: (state) => (taskId, planId) => {
            return state.tasks[taskId].plans.find(plan => plan.id === planId)
        }
    },
    actions: {
        setField(field) {
            this.field = field
        },
        reset() {
            this.loading = true
            this.field = null
            this.tasks = {}
            this.tasksByPeriod = {}
            this.loadingPeriods = {}
            this.disabledTasks = []
            this.isListOpened = false
            this.routePlannerChecked = false
            this.activeTasksPeriod = '6m'
            this.routePlannerTask = null
            this.pendingScrollTaskId = null
            this.pendingScrollCheck = false
            this.pendingScrollNexatMachineId = null

            const tfs = useTasksFilterStore()
            // Reset tasks filter store
            tfs.reset()
        },
        // Get only basic tasks information
        async getFieldTasks(fieldId) {
            try {
                this.loading = true
                if (fieldId == null || fieldId === '') {
                    console.error('No field ID provided');
                    this.loading = false;
                    return;
                }

                let res = await taskAPI.getTasks(fieldId)
        
                // If no tasks, return
                if (!res || Object.keys(res).length === 0) {
                    this.tasks = {}
                    this.loading = false
                    return
                }

                this.tasks = {}

                // Get only basic task information
                Object.values(res).forEach(task => {
                    this.tasks[task.id] = task
                });
                
                this.loading = false
            } catch (error) {
                console.error('Error fetching field tasks:', error)
                this.loading = false
            }
        },
        /**
         * Get tasks for a specific period.
         * period: "7d" | "30d" | "90d" | "6m" | "1y" | "all"
         * Lazy-loads per period and groups tasks in tasksByPeriod.
         */
        async getFieldTasksByPeriod(fieldId, period) {
            try {
                if (fieldId == null || fieldId === '' || period == null || period === '') {
                    console.error('No field ID or period provided')
                    return
                }

                if (!this.loadingPeriods) {
                    this.loadingPeriods = {}
                }

                this.loadingPeriods[period] = true

                const res = await taskAPI.getTasks(fieldId, period)

                if (!res || Object.keys(res).length === 0) {
                    this.tasksByPeriod[period] = {}
                    this.loadingPeriods[period] = false
                    return
                }

                const periodTasks = {}

                Object.values(res).forEach(task => {
                    periodTasks[task.id] = task
                    // keep a flat map of all loaded tasks as well
                    this.tasks[task.id] = task
                })

                this.tasksByPeriod[period] = periodTasks
                // sort tasks by id descending (newest first)
                this.tasksByPeriod[period] = Object.values(periodTasks).sort((a, b) => b.id - a.id)
                this.loadingPeriods[period] = false
            } catch (error) {
                console.error('Error fetching field tasks by period:', error)
                if (this.loadingPeriods) {
                    this.loadingPeriods[period] = false
                }
            }
        },
        // Get full task information
        async getFieldTask(taskId) {
            this.loading = true
            try {
                if (taskId == null || taskId === '') {
                    console.error('No task ID provided')
                    this.loading = false
                    return
                }
                
                const res = await taskAPI.getTask(taskId)
                if (!res) {
                    this.loading = false
                    return
                }
                this.loading = false
                return res
            } catch (error) {
                console.error('Error fetching task:', error)
                this.loading = false
            }
        },
        // Get only plans information
        async getTaskPlans(taskId, pathType) {
            this.loadingPlans = true
            try {
                if (taskId == null || taskId === '') {
                    console.error('No task ID provided')
                    this.loadingPlans = false
                    return []
                }
                
                // Get plans for task
                let plans = await taskAPI.getPlans(taskId)
                if (!plans) {
                    this.loadingPlans = false
                    return
                }

                // If the task has plans, save each plan's type by its ID
                const existingPlansMap = {}
                if (this.tasks[taskId] && this.tasks[taskId].plans) {
                    this.tasks[taskId].plans.forEach(plan => {
                        existingPlansMap[plan.id] = plan.type
                    })
                }

                // We want to keep the type of the plan if it already exists, otherwise we want to set it to "unknown"
                const plansList = Array.isArray(plans) ? plans.map(plan => ({
                    ...plan,
                    type: existingPlansMap[plan.id] || 'unknown'
                })) : []

                if (this.tasks[taskId]) {
                    this.tasks[taskId].plans = plansList
                }
                
                // Return filtered plans if needed, otherwise return all plans
                const result = pathType === 'all' ? plansList : plansList.filter(plan => plan.type === pathType)
                this.loadingPlans = false
                return result
            } catch (error) {
                console.error('Error fetching task plans:', error)
                this.loadingPlans = false
                return []
            }
        },
        // Get full plan information
        async getTaskPlan(planId) {
            try {
                if (planId == null || planId === '') {
                    console.error('No plan ID provided')
                    return null;
                }
                
                // Get plan details
                const planDetails = await taskAPI.getPlan(planId)
                return planDetails
            } catch (error) {
                console.error(`Error fetching plan details for plan ID ${planId}:`, error)
                return null
            }
        },
        async previewUploadISOData(taskId, file) {
            const data = new FormData()
            data.append('file', file)
            const res = await isoCtrl.getGridPreviewForTask(taskId, data)
            return res
        },
        async uploadISOData(taskId, file) {
            const data = new FormData()
            data.append('file', file)
            const res = await isoCtrl.uploadISODataToTask(taskId, data)
            return res
        },
        async downloadISOData(taskId) {
            let res = await taskAPI.downloadISODataFromTask(taskId)
            let now = moment().format('YYYY-MM-DD-HH-mm-ss')
            if (res.ok) {
                saveAs(res.data, `ISOBUS-data-${now}.zip`)
            }
            return res
        },
        async downloadLatestWorkDataFromField(fieldId, fieldName, work_type) {
            let res = await taskAPI.downloadLatestWorkDataFromField(fieldId, work_type)
            let now = moment().format('YYYY-MM-DD-HH-mm-ss')
            if (res.ok) {
                saveAs(res.data, `${work_type}-data-for-field-${fieldId}-${fieldName}-at-${now}.zip`)
            }
            return res
        },
        async createSimpleTask(fieldId, name, created, status) {
            this.creatingNewTask = true
            let res = await taskAPI.createTask({
                field_id: fieldId,
                name: name,
                created: created,
                status: status,
            })
            this.creatingNewTask = false
            return res
        },
        async editSimpleTask(taskId, fieldId, name, created, status) {
            let res = await taskAPI.updateTask({
                id: taskId,
                field_id: fieldId,
                name: name,
                created: created,
                status: status,
            })
            return res
        },
        async disablePath(planId) {
            try {
                const data = {
                    id: planId,
                    valid_to: moment().toISOString()
                }
                let res = await taskAPI.updatePlan(data)
                return res
            } catch (error) {
                console.error('Error disabling path:', error)
                return null
            }
        },
        async disableAllFieldPaths() {
            await this.getFieldTasks(this.field.id)
            for (const task of Object.values(this.tasks)) {
                const taskPlans = await this.getTaskPlans(task.id, 'all')
                for (const plan of taskPlans) {
                    await this.disablePath(plan.id)
                }
            }
        },
        async toggleTaskStatus(taskId, status) {
            try {
                const data = {
                    id: taskId,
                    field_id: this.field.id,
                    status: status
                }
                let res = await taskAPI.updateTask(data)
                return res
            } catch (error) {
                console.error('Error disabling task:', error)
                return null
            }
        },
        async getDisabledTasks(fieldId) {
            try {
                const res = await taskAPI.getDisabledTasks(fieldId)
                if (Array.isArray(res)) {
                    this.disabledTasks = res
                } else {
                    this.disabledTasks = []
                }
                return res
            } catch (error) {
                console.error('Error fetching disabled tasks:', error)
                return []
            }
        },
        async getTaskByName(fieldId, name) {
            try {
                const res = await taskAPI.getTaskByName(fieldId, name)
                if (res && res.id) {
                    this.routePlannerTask = res
                } else {
                    this.routePlannerTask = null
                }
            } catch (error) {
                console.error('Error checking task name exists:', error)
                this.routePlannerTask = null
            }
        },
        // Create a default task for path planning
        async createDefaultTask() {
            if (!this.field) {
                console.error('No field provided')
                return null
            }

            const fieldId = this.field.id
            const now = new Date().toISOString()
            const taskName = 'Route planner Task'
            const taskStatus = 'planned'

            const newTask = await this.createSimpleTask(fieldId, taskName, now, taskStatus)
            this.routePlannerTask = newTask.data // Default task for path planning
            return newTask
        },
        async ensureTaskLoaded(taskId) {
            // If the task is already loaded, return it
            if (this.tasks[taskId]) return this.tasks[taskId]

            // If the task is not loaded, load it
            // exp: navigate directly to /field/1/tasks for task 123 but task 123 is older than 7 days
            // by default, tasks are loaded for the last 7 days
            // if the task is not loaded, load it
            const task = await taskAPI.getTask(taskId)
            if (task && task.id) {
                this.tasks[task.id] = task
            }
            return this.tasks[taskId]
        }
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTasksStore, import.meta.hot))
}
